import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAllTools, addModelToProject } from '../../services/aiModels';

const AddExistingToolModal = ({ show, onHide, projectId, onToolAdded }) => {
  const [tools, setTools] = useState([]);
  const [selectedToolId, setSelectedToolId] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (show) {
      fetchAvailableTools();
    }
  }, [show]);

  const fetchAvailableTools = async () => {
    setLoading(true);
    try {
      const allTools = await getAllTools();
      setTools(allTools);
    } catch (err) {
      console.error('Error fetching tools:', err);
      toast.error('Failed to fetch available tools.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTool = async () => {
    if (!selectedToolId) {
      toast.warning('Please select a tool.');
      return;
    }

    setProcessing(true);
    try {
      await addModelToProject(selectedToolId, projectId);
      toast.success('Tool added to project successfully!');
      setSelectedToolId('');
      if (onToolAdded) {
        onToolAdded();
      }
      onHide();
    } catch (err) {
      console.error('Error adding tool to project:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to add tool to project.';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedToolId('');
    onHide();
  };

  if (!show) return null;

  const getStatusBadge = (tool) => {
    if (tool.build_status === 'ready') {
      return <span className="badge bg-success ms-2">Ready</span>;
    } else if (tool.build_status === 'building') {
      return <span className="badge bg-warning ms-2">Building</span>;
    } else if (tool.build_status === 'failed') {
      return <span className="badge bg-danger ms-2">Failed</span>;
    }
    return <span className="badge bg-secondary ms-2">Pending</span>;
  };

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose}></div>
      <div className="modal show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fa fa-wrench me-2"></i>
                Add Existing Tool to Project
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                <i className="fa fa-info-circle me-2"></i>
                Select an existing processing tool to add to this project
              </div>

              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Select Tool</label>
                  <select
                    className="form-select"
                    value={selectedToolId}
                    onChange={(e) => setSelectedToolId(e.target.value)}
                    disabled={processing}
                  >
                    <option value="">-- Choose a tool --</option>
                    {tools.map(tool => (
                      <option key={tool.id} value={tool.id}>
                        {tool.name} ({tool.model_type_display || tool.model_type || 'Tool'})
                        {tool.build_status === 'ready' ? ' \u2713' : ''}
                      </option>
                    ))}
                  </select>

                  {selectedToolId && tools.find(t => t.id === parseInt(selectedToolId)) && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      {(() => {
                        const selectedTool = tools.find(t => t.id === parseInt(selectedToolId));
                        return (
                          <div>
                            <div className="d-flex align-items-center mb-2">
                              <i
                                className={`fa ${selectedTool.icon || 'fa-wrench'} me-2`}
                                style={{ color: selectedTool.color || '#6c757d', fontSize: '1.5rem' }}
                              ></i>
                              <div>
                                <h6 className="mb-0">{selectedTool.name}</h6>
                                {getStatusBadge(selectedTool)}
                              </div>
                            </div>
                            {selectedTool.description && (
                              <p className="text-muted small mb-2">{selectedTool.description}</p>
                            )}
                            <div className="small">
                              <strong>Type:</strong> {selectedTool.model_type_display || selectedTool.model_type || 'N/A'}<br />
                              <strong>Created:</strong> {new Date(selectedTool.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {tools.length === 0 && (
                    <p className="text-muted small mt-2">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      No tools available. Upload a tool first from the Models & Tools page.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddTool}
                disabled={!selectedToolId || processing || loading}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus me-2"></i>
                    Add Tool
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddExistingToolModal;
